import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, CircularProgress, Alert,
    TextField, Button, Card, CardContent, Divider, Grid,
    FormControl, Select, MenuItem, Tabs, Tab
} from '@mui/material';
import { People, Storefront, MonetizationOn, ShoppingCart, Refresh } from '@mui/icons-material';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, CartesianGrid
} from 'recharts';
import { getDashboardStats, getActiveServiceLocations } from '../services/Api';


const REVENUE_COLORS = ['#3b82f6', '#10b981', '#8b5cf6']; // Blue for Grocery, Green for Food, Purple for Service
const BOOKING_COLORS = ['#6366f1', '#f59e0b', '#ec4899']; // Indigo for Grocery, Amber for Food, Pink for Service

const StatCard = ({ title, value, icon, color, subtitle }) => (
    <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%', width: 'fit-content', minWidth: 200 }}>
        <CardContent sx={{ p: 2, pr: 4, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}.light`, color: `${color}.dark` }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase" letterSpacing={0.5} mb={0.5} display="block">
                    {title}
                </Typography>
                <Typography variant="h5" fontWeight="600" color="text.primary">
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </CardContent>
    </Card>
);

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [locationId, setLocationId] = useState('');
    const [locations, setLocations] = useState([]);
    const [chartTab, setChartTab] = useState(0);

    const fetchStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getDashboardStats(fromDate, toDate, locationId);
            if (response.success || response.data) {
                setStats(response.data || response);
            } else {
                setError(response?.message || 'Failed to fetch dashboard data.');
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Error loading dashboard statistics.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const res = await getActiveServiceLocations();
                if (res.success) {
                    setLocations(res.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch locations', err);
            }
        };
        fetchLocations();
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

    const renderDashboard = () => {
        if (!stats) return null;

        const summary = stats.summary || {};
        const monthly = stats.monthlyAnalytics || [];

        const revenueData = [
            { name: 'Grocery', value: summary.revenue?.grocery || 0 },
            { name: 'Food', value: summary.revenue?.food || 0 },
            { name: 'Service', value: summary.revenue?.service || 0 },
        ].filter(item => item.value > 0);

        const bookingsData = [
            { name: 'Grocery', value: summary.bookings?.grocery || 0 },
            { name: 'Food', value: summary.bookings?.food || 0 },
            { name: 'Service', value: summary.bookings?.service || 0 },
        ].filter(item => item.value > 0);

        const formatMonthTick = (tickItem) => {
            if (!tickItem) return '';
            const [year, month] = tickItem.split('-');
            const date = new Date(year, month - 1);
            return date.toLocaleString('default', { month: 'short', year: 'numeric' }); // e.g., "Jan 2026"
        };

        return (
            <Box>
                {/* Header for General Stats */}
                <Typography variant="h6" fontWeight="600" mb={2} color="text.secondary">General Metrics</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                    <StatCard
                        title="Total Users"
                        value={summary.totalUsers || 0}
                        icon={<People fontSize="medium" />}
                        color="info"
                    />
                    <StatCard
                        title="Total Partners"
                        value={summary.totalPartners || 0}
                        icon={<People fontSize="medium" />}
                        color="warning"
                    />
                    <StatCard
                        title="Total Vendors"
                        value={summary.vendors?.total || 0}
                        icon={<Storefront fontSize="medium" />}
                        color="primary"
                    />
                </Box>

                {/* Header for Revenue */}
                <Typography variant="h6" fontWeight="600" mb={2} color="text.secondary">Revenue Analytics</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', gap: 2, mb: 4, pb: 1 }}>
                    <StatCard
                        title="Gross Revenue"
                        value={formatCurrency(summary.revenue?.total)}
                        icon={<MonetizationOn fontSize="medium" />}
                        color="success"
                    />
                    <StatCard
                        title="Grocery Earnings"
                        value={formatCurrency(summary.revenue?.grocery)}
                        icon={<MonetizationOn fontSize="medium" />}
                        color="info"
                    />
                    <StatCard
                        title="Food Earnings"
                        value={formatCurrency(summary.revenue?.food)}
                        icon={<MonetizationOn fontSize="medium" />}
                        color="warning"
                    />
                    <StatCard
                        title="Service Earnings"
                        value={formatCurrency(summary.revenue?.service)}
                        icon={<MonetizationOn fontSize="medium" />}
                        color="secondary"
                    />
                </Box>

                {/* Header for Bookings */}
                <Typography variant="h6" fontWeight="600" mb={2} color="text.secondary">Booking Volumes</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', gap: 2, mb: 2, pb: 1 }}>
                    <StatCard
                        title="Total Bookings"
                        value={summary.bookings?.total || 0}
                        icon={<ShoppingCart fontSize="medium" />}
                        color="primary"
                    />
                    <StatCard
                        title="Grocery Bookings"
                        value={summary.bookings?.grocery || 0}
                        icon={<ShoppingCart fontSize="medium" />}
                        color="info"
                    />
                    <StatCard
                        title="Food Bookings"
                        value={summary.bookings?.food || 0}
                        icon={<ShoppingCart fontSize="medium" />}
                        color="warning"
                    />
                    <StatCard
                        title="Service Bookings"
                        value={summary.bookings?.service || 0}
                        icon={<ShoppingCart fontSize="medium" />}
                        color="secondary"
                    />
                </Box>

                {/* Vendor Split Section */}
                <Typography variant="h6" fontWeight="600" mb={2} color="text.secondary">Vendor Allocation</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                    <StatCard
                        title="Grocery Stores"
                        value={summary.vendors?.grocery || 0}
                        icon={<Storefront fontSize="medium" />}
                        color="info"
                    />
                    <StatCard
                        title="Food Restaurants"
                        value={summary.vendors?.food || 0}
                        icon={<Storefront fontSize="medium" />}
                        color="warning"
                    />
                </Box>

                {/* Main Analytics Row */}
                <Grid container spacing={3}>
                    {/* Monthly Analytics Tabbed Chart */}
                    <Grid size={{ xs: 12, lg: 8 }}>
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6" fontWeight="600">Monthly Trends</Typography>
                                <Tabs
                                    value={chartTab}
                                    onChange={(_, v) => setChartTab(v)}
                                    sx={{
                                        minHeight: 36,
                                        bgcolor: 'rgba(0,0,0,0.04)',
                                        borderRadius: 2,
                                        p: 0.5,
                                        '& .MuiTab-root': { minHeight: 30, py: 0.5, px: 2, borderRadius: 1.5, fontSize: 13, fontWeight: 500, color: 'text.secondary', textTransform: 'none' },
                                        '& .Mui-selected': { bgcolor: 'white', color: 'primary.main', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
                                        '& .MuiTabs-indicator': { display: 'none' },
                                    }}
                                >
                                    <Tab label="Bookings" />
                                    <Tab label="Revenue" />
                                </Tabs>
                            </Box>
                            <Box sx={{ height: 380, width: '100%' }}>
                                {monthly.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        {chartTab === 0 ? (
                                            <BarChart data={monthly} margin={{ top: 10, right: 20, left: 0, bottom: 5 }} barCategoryGap="30%" barGap={3}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                                <XAxis dataKey="month" axisLine={false} tickLine={false} dy={8} tick={{ fontSize: 12, fill: '#9ca3af' }} tickFormatter={formatMonthTick} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} width={35} />
                                                <Tooltip
                                                    contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', fontSize: 12 }}
                                                    labelFormatter={formatMonthTick}
                                                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                                                />
                                                <Legend wrapperStyle={{ paddingTop: '10px', fontSize: 12 }} iconType="circle" iconSize={8} />
                                                <Bar dataKey="foodBookings" name="Food" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={28} />
                                                <Bar dataKey="groceryBookings" name="Grocery" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={28} />
                                                <Bar dataKey="serviceBookings" name="Service" fill="#ec4899" radius={[4, 4, 0, 0]} maxBarSize={28} />
                                            </BarChart>
                                        ) : (
                                            <BarChart data={monthly} margin={{ top: 10, right: 20, left: 0, bottom: 5 }} barCategoryGap="30%" barGap={3}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                                <XAxis dataKey="month" axisLine={false} tickLine={false} dy={8} tick={{ fontSize: 12, fill: '#9ca3af' }} tickFormatter={formatMonthTick} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} width={55} tickFormatter={(v) => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                                                <Tooltip
                                                    contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', fontSize: 12 }}
                                                    labelFormatter={formatMonthTick}
                                                    formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, undefined]}
                                                    cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                                                />
                                                <Legend wrapperStyle={{ paddingTop: '10px', fontSize: 12 }} iconType="circle" iconSize={8} />
                                                <Bar dataKey="foodRevenue" name="Food" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={28} />
                                                <Bar dataKey="groceryRevenue" name="Grocery" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={28} />
                                                <Bar dataKey="serviceRevenue" name="Service" fill="#8b5cf6" radius={[4, 4, 0, 0]} maxBarSize={28} />
                                            </BarChart>
                                        )}
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography color="text.secondary">No monthly data available for selected range</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Split Analytics Pie Charts */}
                    <Grid size={{ xs: 12, lg: 4 }}>
                        <Grid container spacing={3}>
                            {/* Revenue Split */}
                            <Grid size={{ xs: 12 }}>
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="h6" fontWeight="600" align="center" mb={2}>Revenue Allocation</Typography>
                                    <Box sx={{ height: 200, width: '100%' }}>
                                        {revenueData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={revenueData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={80}
                                                        paddingAngle={5}
                                                        dataKey="value"
                                                    >
                                                        {revenueData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={REVENUE_COLORS[index % REVENUE_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                                    <Legend verticalAlign="bottom" height={36} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Typography align="center" color="text.secondary" sx={{ mt: 8 }}>No revenue data</Typography>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>

                            {/* Bookings Split */}
                            <Grid size={{ xs: 12 }}>
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="h6" fontWeight="600" align="center" mb={2}>Booking Distribution</Typography>
                                    <Box sx={{ height: 200, width: '100%' }}>
                                        {bookingsData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={bookingsData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={80}
                                                        paddingAngle={5}
                                                        dataKey="value"
                                                    >
                                                        {bookingsData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={BOOKING_COLORS[index % BOOKING_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                    <Legend verticalAlign="bottom" height={36} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <Typography align="center" color="text.secondary" sx={{ mt: 8 }}>No booking data</Typography>
                                        )}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <Box>
            {/* Dashboard Header & Filters */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 3, gap: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight="300" color="text.primary" gutterBottom>
                        CareOff Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Comprehensive overview of your platform's operational flow and performance
                    </Typography>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                    <TextField
                        label="From Date"
                        type="date"
                        size="small"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: '9999-12-31' }}
                        sx={{ bgcolor: 'white', borderRadius: 1, width: { xs: '100%', sm: 'auto' } }}
                    />
                    <TextField
                        label="To Date"
                        type="date"
                        size="small"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: '9999-12-31' }}
                        sx={{ bgcolor: 'white', borderRadius: 1, width: { xs: '100%', sm: 'auto' } }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'white', borderRadius: 1 }}>
                        <Select
                            displayEmpty
                            value={locationId}
                            onChange={(e) => setLocationId(e.target.value)}
                        >
                            <MenuItem value="">All Locations</MenuItem>
                            {locations.map((loc) => (
                                <MenuItem key={loc.id} value={loc.id}>
                                    {loc.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={fetchStats}
                        disabled={loading}
                        startIcon={<Refresh />}
                        sx={{ borderRadius: 2, width: { xs: '100%', sm: 'auto' }, textTransform: 'none', px: 3, height: '40px' }}
                    >
                        Apply Filters
                    </Button>
                </Box>
            </Box>

            <Divider sx={{ mb: 4 }} />


            {/* Dashboard Content */}
            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                renderDashboard()
            )}
        </Box>
    );
};

export default Dashboard;
